/**
 * Playout on the wire (PLAN §5.2, §5.3, §6.6): operator commands, the playout
 * state a UI renders, and the as-run log.
 *
 * **Commands (§6.6: "REST command → the active production. Explicit
 * ack/nack. Never merged").** One route, `POST /productions/{id}/commands`,
 * whose body is a union discriminated on `type` — §5.3's v1 operations plus
 * D19's `syncToInstance`. Every command carries a client-minted `commandId`:
 * the production answers a repeated id with the answer it gave the first
 * time and does not run it twice, so a client may retry a command whose
 * answer it lost. The answer is an ack (`accepted: true`, the playout `seq`
 * that includes the command's effect) or a nack (`accepted: false`, a
 * machine-readable `reason` and a message). A nack is a 200: the production
 * heard the command and said no. Failing to REACH the production is an error
 * (`unavailable`), because then nobody said anything.
 *
 * **Playout state.** Owned by the active production process alone (§6.6),
 * sequence-numbered: `seq` increases with every change. The shape is a
 * projection of the engine's (`@acp/engine/state.js`'s `EngineState` and
 * `timings.ts`'s `getTimings()`): the part and piece instances of §5.2
 * (`@acp/domain/instances.js` — the engine's instances without its private
 * bookkeeping), the activation, `takeCount`/`lastTakeTime`/`startedPlayback`/
 * `autoNext`/`history`, the timings of §7.4 and the device statuses of §7.2 —
 * so mapping the engine's state onto it is a copy, not an interpretation. `schemaVersion` is bumped only on a breaking change
 * (never, while pinned productions may speak the old one, §4.3/§6.8);
 * additions are additive.
 *
 * **As-run (§5.4, §6.6).** Append-only events with ULID ids, read with a
 * cursor (the last id seen). The production writes them at least once and
 * idempotently (a repeated id is kept once).
 */
import { partInstanceWireSchema, pieceInstanceWireSchema } from './instances.js';
import { epochMsSchema, fieldPathSchema, jsonValueSchema, opaqueIdSchema, ulidSchema } from '@acp/domain/primitives.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import type { PermissionIdWire } from './permissions.js';

// ─── commands ───

/** A client-minted command id: the idempotency key of a command (§6.6). */
export const commandIdWireSchema = opaqueIdSchema;

/** A config-level id: a piece type, an action, a layer, a device (the studio config's keys, D22). */
export const configIdWireSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/, 'config ids are 1-128 of [A-Za-z0-9_.:-]');

/** The most parts `moveNext` moves in one command: beyond this, name the part (`setNext`). */
export const MAX_MOVE_NEXT = 100;

function command<T extends string, S extends z.ZodRawShape>(type: T, shape: S) {
  return z.object({ type: z.literal(type), commandId: commandIdWireSchema, ...shape }).strict();
}

export const activateCommandWireSchema = command('activate', {
  /** Rehearsal mode (Sofie's): playout runs, nothing airs for real. Null: the production's own `rehearsal`. */
  rehearsal: z.boolean().nullable(),
});
export const deactivateCommandWireSchema = command('deactivate', {});
/** Back to the top: a fresh activation with no played instances (§5.3). */
export const resetCommandWireSchema = command('reset', {});
/** Next becomes current. Guarded by `minimumTakeSpan` (§5.3). */
export const takeCommandWireSchema = command('take', {});
export const setNextCommandWireSchema = command('setNext', { partId: ulidSchema });
export const moveNextCommandWireSchema = command('moveNext', {
  /** Parts forward (+) or back (−) from the current next. */
  delta: z
    .number()
    .int()
    .min(-MAX_MOVE_NEXT)
    .max(MAX_MOVE_NEXT)
    .refine((d) => d !== 0, { message: 'delta must not be 0' }),
});
/** Next becomes the first part of this segment, now. */
export const setNextSegmentCommandWireSchema = command('setNextSegment', { segmentId: ulidSchema });
/** When the current segment ends, continue with this one (instead of the following segment). */
export const queueNextSegmentCommandWireSchema = command('queueNextSegment', { segmentId: ulidSchema });
export const adlibModeWireSchema = z.enum(['now', 'queue']);
/**
 * Play a piece of `pieceType` with `params` (validated against the piece
 * type's param schema by the production): `now` into the current part,
 * `queue` as an adlib part inserted as next (§5.1).
 */
export const adlibCommandWireSchema = command('adlib', {
  pieceType: configIdWireSchema,
  params: z.record(z.string(), jsonValueSchema),
  mode: adlibModeWireSchema,
});
/**
 * Play an adlib ROW of the playlist (PLAN §5.1 "AdLib pieces": a part's own
 * adlib, or a global one under the rundown): its piece type and params, its
 * `expectedDuration` and the piece fields it writes itself, as the row says
 * when the production runs the command — `now` or `queue` as `adlib`. The
 * engine's `adlibFromRow`. A production pinned before this command answers
 * `unsupported-command` (§4.3) — on a node that knows the command. A node
 * (state service) older than it cannot parse it: over REST that is a
 * `validation-failed`, and relayed from the cloud over the site link the
 * frame is dropped, so the cloud answers `unavailable` after its timeout.
 * Update nodes before relying on it from the cloud (PLAN §13).
 */
export const adlibFromRowCommandWireSchema = command('adlibFromRow', {
  adlibId: ulidSchema,
  mode: adlibModeWireSchema,
});
export const stopPiecesOnLayerCommandWireSchema = command('stopPiecesOnLayer', { layer: configIdWireSchema });
/** Skip the next piece due to start in the current part (§5.3). */
export const disableNextPieceCommandWireSchema = command('disableNextPiece', {});
/** Run a macro action of the studio config (`actions`, D22). */
export const executeActionCommandWireSchema = command('executeAction', { actionId: configIdWireSchema });
/**
 * Sync the named live-editable fields of the current part's definition into
 * its running instance (D19: "Update on air?"). Needs `playout.take`.
 */
export const syncToInstanceCommandWireSchema = command('syncToInstance', {
  partInstanceId: ulidSchema,
  fields: z.array(fieldPathSchema).min(1).max(50),
});

export const playoutCommandWireSchema = z.discriminatedUnion('type', [
  activateCommandWireSchema,
  deactivateCommandWireSchema,
  resetCommandWireSchema,
  takeCommandWireSchema,
  setNextCommandWireSchema,
  moveNextCommandWireSchema,
  setNextSegmentCommandWireSchema,
  queueNextSegmentCommandWireSchema,
  adlibCommandWireSchema,
  adlibFromRowCommandWireSchema,
  stopPiecesOnLayerCommandWireSchema,
  disableNextPieceCommandWireSchema,
  executeActionCommandWireSchema,
  syncToInstanceCommandWireSchema,
]);
export type PlayoutCommandWire = z.infer<typeof playoutCommandWireSchema>;
export type PlayoutCommandTypeWire = PlayoutCommandWire['type'];

/**
 * The permission each command needs on the production's studio (D14, D19):
 * `playout.activate` for the activation lifecycle, `playout.take` for
 * everything an operator does on air.
 */
export const COMMAND_PERMISSIONS: Readonly<Record<PlayoutCommandTypeWire, PermissionIdWire>> = {
  activate: 'playout.activate',
  deactivate: 'playout.activate',
  reset: 'playout.activate',
  take: 'playout.take',
  setNext: 'playout.take',
  moveNext: 'playout.take',
  setNextSegment: 'playout.take',
  queueNextSegment: 'playout.take',
  adlib: 'playout.take',
  adlibFromRow: 'playout.take',
  stopPiecesOnLayer: 'playout.take',
  disableNextPiece: 'playout.take',
  executeAction: 'playout.take',
  syncToInstance: 'playout.take',
};

/** The permissions `POST /productions/{id}/commands` can require, for its gate and docs. */
export const PLAYOUT_COMMAND_PERMISSIONS: readonly PermissionIdWire[] = ['playout.take', 'playout.activate'];

/**
 * Why a production refused a command. Open (a production of a newer version
 * may give a reason this build does not know, §4.3), with these known values:
 *
 *   production-ended   the production has ended; it takes no more commands
 *   not-active         the playlist is not activated (take, next, adlib…)
 *   already-active     activate while active
 *   no-next            take with nothing set as next
 *   take-too-soon      within `minimumTakeSpan` of the previous take (§5.3)
 *   unknown-part       setNext/moveNext/segment to a part or segment that is not in the playlist (or is invalid)
 *   unknown-piece-type adlib of a piece type the config snapshot lacks
 *   unknown-action     executeAction of an action the config snapshot lacks
 *   unknown-adlib      adlibFromRow of a row that is no adlib of the playlist (deleted, or never there)
 *   invalid-params     the adlib's params fail the piece type's schema
 *   unsupported-command a command this production's (pinned, older) version does not know (§4.3)
 *   unknown-instance   syncToInstance of an instance that is not current or next
 *   not-live-editable  syncToInstance of a field that is not live-editable (D19)
 *   nothing-to-disable disableNextPiece with no piece left to start
 *   not-active-node    this node is the production's STANDBY (PLAN §8.3: "the standby rejects them with notActive →
 *                      activeNode"): the message names the active node; send it there (`redundancy.activeNode` of the state)
 */
export const nackReasonWireSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/);

export const commandResultWireSchema = z.discriminatedUnion('accepted', [
  z
    .object({
      commandId: commandIdWireSchema,
      accepted: z.literal(true),
      /** The playout `seq` whose state includes this command's effect. */
      seq: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      commandId: commandIdWireSchema,
      accepted: z.literal(false),
      reason: nackReasonWireSchema,
      message: z.string().max(1000),
    })
    .strict(),
]);
export type CommandResultWire = z.infer<typeof commandResultWireSchema>;

// ─── playout state ───

/** The current wire version of `PlayoutStateWire`. */
export const PLAYOUT_STATE_SCHEMA_VERSION = 1;

/** A part instance with its piece instances (§5.2): the engine's own shapes. */
export const partInstanceStateWireSchema = z
  .object({
    instance: partInstanceWireSchema,
    pieces: z.array(pieceInstanceWireSchema),
  })
  .strict();
export type PartInstanceStateWire = z.infer<typeof partInstanceStateWireSchema>;

export const activationWireSchema = z
  .object({
    status: z.enum(['inactive', 'active', 'rehearsal']),
    /** The activation the instances belong to (a reset starts a new one); null while inactive. */
    activationId: ulidSchema.nullable(),
    activatedAt: epochMsSchema.nullable(),
  })
  .strict();

const signedMsSchema = z.number().int();

/** The on-air part's clock (the engine's `OnAirTimings`). */
export const onAirTimingWireSchema = z
  .object({
    partInstanceId: ulidSchema,
    partId: ulidSchema,
    startedAt: epochMsSchema,
    elapsedMs: z.number().int().nonnegative(),
    expectedDurationMs: z.number().int().nonnegative().nullable(),
    /** Countdown to the expected end; negative once over; null without an expected duration. */
    remainingMs: signedMsSchema.nullable(),
    /** When an auto-next hands over, if it will. */
    autoNextAt: epochMsSchema.nullable(),
  })
  .strict();

/** A piece's countdown (the engine's `PieceCountdown`). */
export const pieceCountdownWireSchema = z
  .object({
    pieceInstanceId: ulidSchema,
    sourceLayer: z.string().max(128),
    startsAt: epochMsSchema,
    endsAt: epochMsSchema.nullable(),
    /** Until it starts; ≤ 0 once it has. */
    startsInMs: signedMsSchema,
    /** Until it ends; null when open-ended. */
    remainingMs: signedMsSchema.nullable(),
  })
  .strict();

/** A segment's budget and pace (the engine's `SegmentTimings`). */
export const segmentTimingWireSchema = z
  .object({
    segmentId: ulidSchema,
    budgetMs: z.number().int().nonnegative().nullable(),
    expectedMs: z.number().int().nonnegative(),
    playedMs: z.number().int().nonnegative(),
    remainingMs: signedMsSchema,
    overUnderMs: signedMsSchema,
  })
  .strict();

/**
 * Timings (§7.4) as the engine computes them at `now` (its `PlaylistTimings`,
 * `getTimings()`): a UI counts down locally from these and re-bases on the
 * next state.
 */
export const playoutTimingWireSchema = z
  .object({
    /** The production's clock when this state was produced. */
    now: epochMsSchema,
    /** The playlist's timing mode (PLAN §5.1); `none` for a rundown run alone. */
    mode: z.enum(['none', 'forward-time', 'back-time', 'duration']),
    /** The planned length of every timed part. */
    totalExpectedMs: z.number().int().nonnegative(),
    playedMs: z.number().int().nonnegative(),
    remainingMs: signedMsSchema,
    /** The end the playlist's timing asks for; null when it names none. */
    expectedEnd: epochMsSchema.nullable(),
    /** When it will end at the current pace; null before it starts. */
    projectedEnd: epochMsSchema.nullable(),
    /** Over (+) / under (−) against the timing, ms; null when there is nothing to hold it to. */
    overUnderMs: signedMsSchema.nullable(),
    onAir: onAirTimingWireSchema.nullable(),
    pieces: z.array(pieceCountdownWireSchema),
    segments: z.array(segmentTimingWireSchema),
  })
  .strict();

/** One part as played in this activation (the engine's `PlayedPart`): the as-played record the timings count. */
export const playedPartWireSchema = z
  .object({
    partInstanceId: ulidSchema,
    partId: ulidSchema,
    segmentId: ulidSchema,
    takeCount: z.number().int().nonnegative(),
    untimed: z.boolean(),
    startedAt: epochMsSchema,
    stoppedAt: epochMsSchema,
  })
  .strict();

/** One device's status (§7.2): connectivity and health as the production sees it. */
export const deviceStatusWireSchema = z
  .object({
    deviceId: configIdWireSchema,
    /** The driver type from the config (`vmix`, `casparcg`, `sim-video-server`, …). */
    type: z.string().min(1).max(64),
    connected: z.boolean(),
    /** Open (D37 §4.3): a newer driver layer may grade a device in a way this reader does not know. */
    status: openEnum(['good', 'warning', 'bad']),
    /** Human-readable detail; null when there is nothing to say. */
    message: z.string().max(1000).nullable(),
    /** When the production last heard from it; null if never. */
    lastSeen: epochMsSchema.nullable(),
  })
  .strict();
export type DeviceStatusWire = z.infer<typeof deviceStatusWireSchema>;

/**
 * A production's main/backup pair as the node answering sees it (PLAN §8,
 * §8.6: "shows which node is active per production … the SPA follows role
 * changes pushed over WS"). Absent from a production outside a pair (a single
 * node, the in-memory simulation, a process pinned before phase 5).
 */
export const playoutRedundancyWireSchema = z
  .object({
    /** This node's role for the production: `active` drives the devices, `standby` follows in shadow. */
    role: z.enum(['active', 'standby']),
    /** The node answering (whose process this state comes from). */
    node: z.string().min(1).max(128),
    /** The node the production record names active (where commands go). */
    activeNode: z.string().min(1).max(128).nullable(),
    /** The failover term (D9): bumped on every promotion. */
    term: z.number().int().nonnegative(),
    /** The other node of the pair; null when running without one. */
    peerNode: z.string().min(1).max(128).nullable(),
    /**
     * `protected`: a standby follows the active's stream and is warm. `syncing`: one is connecting or catching up.
     * `unprotected`: none — the peer is down or has no standby (§8.2: "the production runs unprotected, and the UI shows a warning").
     */
    protection: z.enum(['protected', 'syncing', 'unprotected']),
    /** D9's per-production "manual only": no automatic failover. */
    manualOnly: z.boolean(),
    /** What operators should know about the pair now (no witness reachable, a partition, …); null when all is well. */
    alarm: z.string().max(500).nullable(),
  })
  .strict();
export type PlayoutRedundancyWire = z.infer<typeof playoutRedundancyWireSchema>;

export const playoutStateWireSchema = z
  .object({
    schemaVersion: z.literal(PLAYOUT_STATE_SCHEMA_VERSION),
    productionId: ulidSchema,
    /** Increases with every change of this state (§6.6's sequence-numbered stream). */
    seq: z.number().int().nonnegative(),
    activation: activationWireSchema,
    previous: partInstanceStateWireSchema.nullable(),
    current: partInstanceStateWireSchema.nullable(),
    next: partInstanceStateWireSchema.nullable(),
    /** Whether next was set by an operator (setNext, moveNext…) rather than by the running order. */
    nextSetManually: z.boolean(),
    /** The segment queued by `queueNextSegment`, if any. */
    queuedSegmentId: ulidSchema.nullable(),
    /** Takes in this activation (the on-air instance's `takeCount`). */
    takeCount: z.number().int().nonnegative(),
    lastTakeTime: epochMsSchema.nullable(),
    /** The first timed take of this activation (Sofie's `startedPlayback`): where over/under counts from. */
    startedPlayback: epochMsSchema.nullable(),
    /** A pending automatic take (the engine's `autoNext`): when, and from which on-air instance. */
    autoNext: z.object({ at: epochMsSchema, fromPartInstanceId: ulidSchema }).strict().nullable(),
    /** The as-played record of this activation, oldest first. */
    history: z.array(playedPartWireSchema),
    timing: playoutTimingWireSchema,
    devices: z.array(deviceStatusWireSchema),
    /** The main/backup pair (added in phase 5; absent outside a pair). */
    redundancy: playoutRedundancyWireSchema.optional(),
  })
  .strict();
export type PlayoutStateWire = z.infer<typeof playoutStateWireSchema>;

export const playoutStateResponseWireSchema = z
  .object({
    state: playoutStateWireSchema,
    /**
     * Present when `state` is not live but the last snapshot the cloud holds,
     * because the production's node cannot be reached (wave 4b-1): read-only,
     * as of `since` (when the cloud last heard it), and why.
     */
    stale: z.object({ since: z.iso.datetime({ offset: true }), reason: z.string().max(1000) }).strict().optional(),
  })
  .strict();

/** The members of a playout state a delta may carry: everything but its envelope (`schemaVersion`, `productionId`, `seq`). */
export const playoutStateMembersWireSchema = playoutStateWireSchema.omit({ schemaVersion: true, productionId: true, seq: true });
export type PlayoutStateMembersWire = z.infer<typeof playoutStateMembersWireSchema>;
/** A delta's `changes`: the members that changed, each whole. */
export type PlayoutStateChangesWire = Partial<PlayoutStateMembersWire>;

/** Every member name, from the schema itself: a member added to the state is in every delta that changes it. */
export const PLAYOUT_STATE_MEMBERS = playoutStateMembersWireSchema.keyof().options;

/**
 * The members of `after` that differ from `before`, each whole — what a
 * `delta` from `before.seq` to `after.seq` carries, so that applying it to
 * `before` gives `after` (one definition for every producer of deltas).
 */
export function changedPlayoutMembers(before: PlayoutStateWire, after: PlayoutStateWire): PlayoutStateChangesWire {
  const changes: PlayoutStateChangesWire = {};
  for (const key of PLAYOUT_STATE_MEMBERS) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) Object.assign(changes, { [key]: after[key] });
  }
  return changes;
}

/**
 * A change to the playout state on the WS stream (`playout.state`): the whole
 * state, or the top-level members that changed since `fromSeq`. A client
 * applies a delta only on top of exactly `fromSeq`; any other gap means it
 * missed a frame, and it re-reads `GET /productions/{id}/playout`.
 */
export const playoutStateChangeWireSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('full'), state: playoutStateWireSchema }).strict(),
  z
    .object({
      kind: z.literal('delta'),
      fromSeq: z.number().int().nonnegative(),
      seq: z.number().int().positive(),
      /** The members that changed, each whole (a shallow merge over the state at `fromSeq`). */
      changes: playoutStateMembersWireSchema.partial().strict(),
    })
    .strict(),
]);
export type PlayoutStateChangeWire = z.infer<typeof playoutStateChangeWireSchema>;

// ─── as-run ───

/**
 * Known as-run event types (open, as nack reasons): `production.created`,
 * `production.ended`, `command` (an operator command the production accepted,
 * with who sent it), `activated`, `deactivated`, `reset`, `take`,
 * `part.started`, `part.ended`, `piece.started`, `piece.stopped`.
 */
export const asRunTypeWireSchema = z.string().regex(/^[a-z][a-z0-9.-]{0,63}$/);

export const asRunEventWireSchema = z
  .object({
    /** A ULID: unique, and the log's order. */
    eventId: ulidSchema,
    type: asRunTypeWireSchema,
    /** When it happened (epoch ms, the writer's clock). */
    at: epochMsSchema,
    /** Who caused it, for operator actions; null for the engine's own events. */
    userId: z.string().min(1).max(128).nullable(),
    /** The command it records (`command` events). */
    commandId: commandIdWireSchema.nullable(),
    partInstanceId: ulidSchema.nullable(),
    pieceInstanceId: ulidSchema.nullable(),
    /** Type-specific detail. */
    detail: z.record(z.string(), jsonValueSchema),
  })
  .strict();
export type AsRunEventWire = z.infer<typeof asRunEventWireSchema>;

export const asRunQueryWireSchema = z
  .object({
    cursor: ulidSchema.optional().describe('Only events after this one: the previous page’s `cursor`. Absent: from the start.'),
    limit: z.coerce.number().int().min(1).max(1000).optional().describe('Events per page, 1–1000; default 500.'),
  })
  .strict();

export const asRunResponseWireSchema = z
  .object({
    /** Oldest first. */
    events: z.array(asRunEventWireSchema),
    /** Pass as `cursor` to continue; null when the log is empty and no cursor was given. */
    cursor: ulidSchema.nullable(),
    /** Whether more events were already there beyond this page. */
    more: z.boolean(),
  })
  .strict();
